// @flow
import * as React from "react";
import { extendObservable, action, runInAction, computed } from "mobx";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NetworkState from "./NetworkState";
import { LEVEL_ROMAN_NUMERAL } from "../constants/levels";
import TagList from "./TagList";
import BuyRate from "./BuyRate";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { RATE_TYPE_OPTIONS_2_BY_ID } from "../constants/rateTypes";
import { supportEmail } from "../constants";

const baseQuery = (
  currencyType: string = "Default",
  historyFrom: ?Date = null,
  historyTo: ?Date = null,
  fragmentNames: string[] = []
) => {
  let params = [];
  let args = [];
  let historyFragment = "";

  params.push("$searchId: Int!");
  args.push("id: $searchId");

  if (currencyType && currencyType !== "Default") {
    params.push("$convertTo: String!");
    args.push("convertTo: $convertTo");
  }

  if (localStorage.getItem("isPtAdmin") === "true") {
    params.push("$section: UserSectionTypeEnum");
    args.push("section: $section");
  }

  if (historyFrom || historyTo) {
    const filters = [];
    if (historyFrom) {
      params.push("$resultsHistoryFrom: DateTime!");
      filters.push("fromDate: $resultsHistoryFrom");
    }
    if (historyTo) {
      params.push("$resultsHistoryTo: DateTime!");
      filters.push("toDate: $resultsHistoryTo");
    }
    historyFragment = `
    fragment history on MarketRatesNode {
      history(filters: {${filters.join(
        ", "
      )}}, order: [{field: CREATED, direction: DESC}]) {
        revNum
        numResults
        created
        level {
          legacyId
          value
        }
        ...payRateHistory
        ...billRateHistory
        ...markupPctHistory
      }
    }
    `;
  }

  const queryParams = params.join(", ");
  const queryArgs = args.join(", ");

  return `
  fragment payRate on MarketRatesNode {
    payRateMin
    payRateMid
    payRateAvg
    payRateMax
  }

  fragment markupPct on MarketRatesNode {
    markupPctMin
    markupPctMid
    markupPctAvg
    markupPctMax
  }

  fragment billRate on MarketRatesNode {
    billRateMin
    billRateMid
    billRateAvg
    billRateMax
  }

  fragment payRateHistory on MarketChangeNode {
    payRateMin
    payRateMid
    payRateAvg
    payRateMax
  }

  fragment markupPctHistory on MarketChangeNode {
    markupPctMin
    markupPctMid
    markupPctAvg
    markupPctMax
  }

  fragment billRateHistory on MarketChangeNode {
    billRateMin
    billRateMid
    billRateAvg
    billRateMax
  }

  ${historyFragment}

  query searchDetail(${queryParams}) {
    viewer {
      savedsearch(${queryArgs}) {
        searchId
        canRevert
        needsUpdate
        rateType
        currency {
          iso
          symbol
        }
        marketrates {
          id
          level {
            legacyId
            value
          }
          needsUpdate
          numResults
          ...payRate
          ...billRate
          ...markupPct
          ${historyFragment !== "" ? "...history" : ""}
        }
        marketchanges {
          level {
            legacyId
            value
          }
          ...payRateHistory
          ...billRateHistory
          ...markupPctHistory
        }
      }
    }
  }
  `;
};

const queries = {
  payRate: (currencyType: string = "Default") => {
    return baseQuery(currencyType, null, null, ["payRate"]);
  },
  markupPct: (currencyType: string = "Default") => {
    return baseQuery(currencyType, null, null, ["markupPct"]);
  },
  billRate: (currencyType: string = "Default") => {
    return baseQuery(currencyType, null, null, ["billRate"]);
  },
  fullReload: (
    currencyType: string = "Default",
    historyFrom: ?Date = null,
    historyTo: ?Date = null
  ) => {
    return baseQuery(currencyType, historyFrom, historyTo, [
      "payRate",
      "markupPct",
      "billRate",
    ]);
  },
};

const updateRatesQuery = `
mutation updateRates($searchId: Int!) {
  updateSingleSearchMarketRate(input: {searchId:$searchId}) {
    lastUpdatedTime
  }
}
`;

const undoUpdateRatesQuery = `
mutation undoUpdate($searchId: Int!) {
  undoUpdateSingleSearchMarketRate(input: {searchId: $searchId}) {
    lastUpdatedTime
  }
}
`;

class Currency {
  store: Object;
  iso: string;
  symbol: string;

  constructor(store: Object, object: Object) {
    this.store = store;
    this.iso = object.iso;
    this.symbol = object.symbol;
  }
}

class SearchResultsHistory {
  store: Object;
  revNum: number;
  createdDate: Object;
  numResults: number;
  payRateMin: ?number;
  billRateMin: ?number;
  markupAmtMin: ?number;
  markupPctMin: ?number;
  payRateAvg: ?number;
  billRateAvg: ?number;
  markupAmtAvg: ?number;
  markupPctAvg: ?number;
  payRateMid: ?number;
  billRateMid: ?number;
  markupAmtMid: ?number;
  markupPctMid: ?number;
  payRateMax: ?number;
  billRateMax: ?number;
  markupAmtMax: ?number;
  markupPctMax: ?number;

  constructor(store: Object, object: Object) {
    this.store = store;
    this.revNum = object.revNum;
    this.numResults = object.numResults;
    this.createdDate = object.created;

    extendObservable(this, {
      // Minimums
      payRateMin: object.payRateMin || null,
      billRateMin: object.billRateMin || null,
      markupAmtMin: object.markupAmtMin || null,
      markupPctMin: object.markupPctMin || null,
      // Averages
      payRateAvg: object.payRateAvg || null,
      billRateAvg: object.billRateAvg || null,
      markupAmtAvg: object.markupAmtAvg || null,
      markupPctAvg: object.markupPctAvg || null,
      // Mids
      payRateMid: object.payRateMid || null,
      billRateMid: object.billRateMid || null,
      markupAmtMid: object.markupAmtMid || null,
      markupPctMid: object.markupPctMid || null,
      //Maxes
      payRateMax: object.payRateMax || null,
      billRateMax: object.billRateMax || null,
      markupAmtMax: object.markupAmtMax || null,
      markupPctMax: object.markupPctMax || null,
    });
  }
}

class SearchResultsChange {
  store: Object;
  payRateMinChange: number;
  payRateMidChange: number;
  payRateAvgChange: number;
  payRateMaxChange: number;

  markupPctMinChange: number;
  markupPctMidChange: number;
  markupPctAvgChange: number;
  markupPctMaxChange: number;

  billRateMinChange: number;
  billRateMidChange: number;
  billRateAvgChange: number;
  billRateMaxChange: number;

  payRateMinChangeIcon: string;
  payRateMidChangeIcon: string;
  payRateAvgChangeIcon: string;
  payRateMaxChangeIcon: string;

  markupPctMinChangeIcon: string;
  markupPctMidChangeIcon: string;
  markupPctAvgChangeIcon: string;
  markupPctMaxChangeIcon: string;

  billRateMinChangeIcon: string;
  billRateMidChangeIcon: string;
  billRateAvgChangeIcon: string;
  billRateMaxChangeIcon: string;

  payRateMinChangeType: string;
  payRateMidChangeType: string;
  payRateAvgChangeType: string;
  payRateMaxChangeType: string;

  markupPctMinChangeType: string;
  markupPctMidChangeType: string;
  markupPctAvgChangeType: string;
  markupPctMaxChangeType: string;

  billRateMinChangeType: string;
  billRateMidChangeType: string;
  billRateAvgChangeType: string;
  billRateMaxChangeType: string;

  needsUpdate: boolean;

  constructor(store: Object, object: Object) {
    this.store = store;

    // NOTE: You should be checking needsUpdate on the marketRate or the search itself
    this.needsUpdate = false;

    this.payRateMinChange = 0;
    this.payRateMidChange = 0;
    this.payRateAvgChange = 0;
    this.payRateMaxChange = 0;

    this.markupPctMinChange = 0;
    this.markupPctMidChange = 0;
    this.markupPctAvgChange = 0;
    this.markupPctMaxChange = 0;

    this.billRateMinChange = 0;
    this.billRateMidChange = 0;
    this.billRateAvgChange = 0;
    this.billRateMaxChange = 0;

    this.payRateMinChangeIcon = "minus";
    this.payRateMidChangeIcon = "minus";
    this.payRateAvgChangeIcon = "minus";
    this.payRateMaxChangeIcon = "minus";

    this.markupPctMinChangeIcon = "minus";
    this.markupPctMidChangeIcon = "minus";
    this.markupPctAvgChangeIcon = "minus";
    this.markupPctMaxChangeIcon = "minus";

    this.billRateMinChangeIcon = "minus";
    this.billRateMidChangeIcon = "minus";
    this.billRateAvgChangeIcon = "minus";
    this.billRateMaxChangeIcon = "minus";

    this.payRateMinChangeType = "no-change";
    this.payRateMidChangeType = "no-change";
    this.payRateAvgChangeType = "no-change";
    this.payRateMaxChangeType = "no-change";

    this.markupPctMinChangeType = "no-change";
    this.markupPctMidChangeType = "no-change";
    this.markupPctAvgChangeType = "no-change";
    this.markupPctMaxChangeType = "no-change";

    this.billRateMinChangeType = "no-change";
    this.billRateMidChangeType = "no-change";
    this.billRateAvgChangeType = "no-change";
    this.billRateMaxChangeType = "no-change";

    if (object.latestChange) {
      let latestChange = object.latestChange;
      this.payRateMinChange = latestChange.payRateMin - object.payRateMin;
      this.payRateMidChange = latestChange.payRateMid - object.payRateMid;
      this.payRateAvgChange = latestChange.payRateAvg - object.payRateAvg;
      this.payRateMaxChange = latestChange.payRateMax - object.payRateMax;

      this.markupPctMinChange = latestChange.markupPctMin - object.markupPctMin;
      this.markupPctMidChange = latestChange.markupPctMid - object.markupPctMid;
      this.markupPctAvgChange = latestChange.markupPctAvg - object.markupPctAvg;
      this.markupPctMaxChange = latestChange.markupPctMax - object.markupPctMax;

      this.billRateMinChange = latestChange.billRateMin - object.billRateMin;
      this.billRateMidChange = latestChange.billRateMid - object.billRateMid;
      this.billRateAvgChange = latestChange.billRateAvg - object.billRateAvg;
      this.billRateMaxChange = latestChange.billRateMax - object.billRateMax;

      // TODO: Remove this becuase it doesn't reflect actual "update needed" logic
      this.needsUpdate =
        this.payRateMinChange !== 0 ||
        this.payRateMidChange !== 0 ||
        this.payRateAvgChange !== 0 ||
        this.payRateMaxChange !== 0 ||
        this.markupPctMinChange !== 0 ||
        this.markupPctMidChange !== 0 ||
        this.markupPctAvgChange !== 0 ||
        this.markupPctMaxChange !== 0 ||
        this.billRateMinChange !== 0 ||
        this.billRateMidChange !== 0 ||
        this.billRateAvgChange !== 0 ||
        this.billRateMaxChange !== 0;
    }

    // PAY RATE
    if (this.payRateMinChange > 0) {
      this.payRateMinChangeIcon = "caret-up";
      this.payRateMinChangeType = "positive-change";
    } else if (this.payRateMinChange < 0) {
      this.payRateMinChangeIcon = "caret-down";
      this.payRateMinChangeType = "negative-change";
    }

    if (this.payRateMidChange > 0) {
      this.payRateMidChangeIcon = "caret-up";
      this.payRateMidChangeType = "positive-change";
    } else if (this.payRateMidChange < 0) {
      this.payRateMidChangeIcon = "caret-down";
      this.payRateMidChangeType = "negative-change";
    }

    if (this.payRateAvgChange > 0) {
      this.payRateAvgChangeIcon = "caret-up";
      this.payRateAvgChangeType = "positive-change";
    } else if (this.payRateAvgChange < 0) {
      this.payRateAvgChangeIcon = "caret-down";
      this.payRateAvgChangeType = "negative-change";
    }

    if (this.payRateMaxChange > 0) {
      this.payRateMaxChangeIcon = "caret-up";
      this.payRateMaxChangeType = "positive-change";
    } else if (this.payRateMaxChange < 0) {
      this.payRateMaxChangeIcon = "caret-down";
      this.payRateMaxChangeType = "negative-change";
    }

    // MARKUP
    if (this.markupPctMinChange > 0) {
      this.markupPctMinChangeIcon = "caret-up";
      this.markupPctMinChangeType = "positive-change";
    } else if (this.markupPctMinChange < 0) {
      this.markupPctMinChangeIcon = "caret-down";
      this.markupPctMinChangeType = "negative-change";
    }

    if (this.markupPctMidChange > 0) {
      this.markupPctMidChangeIcon = "caret-up";
      this.markupPctMidChangeType = "positive-change";
    } else if (this.markupPctMidChange < 0) {
      this.markupPctMidChangeIcon = "caret-down";
      this.markupPctMidChangeType = "negative-change";
    }

    if (this.markupPctAvgChange > 0) {
      this.markupPctAvgChangeIcon = "caret-up";
      this.markupPctAvgChangeType = "positive-change";
    } else if (this.markupPctAvgChange < 0) {
      this.markupPctAvgChangeIcon = "caret-down";
      this.markupPctAvgChangeType = "negative-change";
    }

    if (this.markupPctMaxChange > 0) {
      this.markupPctMaxChangeIcon = "caret-up";
      this.markupPctMaxChangeType = "positive-change";
    } else if (this.markupPctMaxChange < 0) {
      this.markupPctMaxChangeIcon = "caret-down";
      this.markupPctMaxChangeType = "negative-change";
    }

    // BILL RATE
    if (this.billRateMinChange > 0) {
      this.billRateMinChangeIcon = "caret-up";
      this.billRateMinChangeType = "positive-change";
    } else if (this.billRateMinChange < 0) {
      this.billRateMinChangeIcon = "caret-down";
      this.billRateMinChangeType = "negative-change";
    }

    if (this.billRateMidChange > 0) {
      this.billRateMidChangeIcon = "caret-up";
      this.billRateMidChangeType = "positive-change";
    } else if (this.billRateMidChange < 0) {
      this.billRateMidChangeIcon = "caret-down";
      this.billRateMidChangeType = "negative-change";
    }

    if (this.billRateAvgChange > 0) {
      this.billRateAvgChangeIcon = "caret-up";
      this.billRateAvgChangeType = "positive-change";
    } else if (this.billRateAvgChange < 0) {
      this.billRateAvgChangeIcon = "caret-down";
      this.billRateAvgChangeType = "negative-change";
    }

    if (this.billRateMaxChange > 0) {
      this.billRateMaxChangeIcon = "caret-up";
      this.billRateMaxChangeType = "positive-change";
    } else if (this.billRateMaxChange < 0) {
      this.billRateMaxChangeIcon = "caret-down";
      this.billRateMaxChangeType = "negative-change";
    }

    extendObservable(this, {});
  }
}

class SearchResults {
  store: Object;
  legacyId: number;
  id: number;
  level: string;
  levelName: string;
  levelRomanNumeral: string;
  needsUpdate: boolean;
  canRevertLatestUpdate: boolean;
  numResults: number;
  numResultsDelta: number;
  payRateMin: ?number;
  billRateMin: ?number;
  markupAmtMin: ?number;
  markupPctMin: ?number;
  payRateAvg: ?number;
  billRateAvg: ?number;
  markupAmtAvg: ?number;
  markupPctAvg: ?number;
  payRateAvgDelta: ?number;
  billRateAvgDelta: ?number;
  markupPctAvgDelta: ?number;
  payRateMid: ?number;
  billRateMid: ?number;
  markupAmtMid: ?number;
  markupPctMid: ?number;
  payRateMax: ?number;
  billRateMax: ?number;
  markupAmtMax: ?number;
  markupPctMax: ?number;
  latestChange: ?SearchResultsHistory;
  change: ?SearchResultsChange;
  history: ?(SearchResultsHistory[]);
  createHistory: (Object[]) => void;
  currency: ?Currency;
  refreshChange: () => void;
  punchoutId: ?number;
  payRateMinAlert: ?number;
  payRateMidAlert: ?number;
  payRateMaxAlert: ?number;
  payRateAvgAlert: ?number;

  constructor(store: Object, object: Object) {
    this.store = store;
    object = object.node;
    this.legacyId = object.level.legacyId;
    this.id = object.id;
    this.level = object.level;
    this.levelName = object.level.value;
    this.levelRomanNumeral = LEVEL_ROMAN_NUMERAL[object.level.value];
    this.numResults = object.numResults;
    this.needsUpdate = object.needsUpdate;
    this.numResultsDelta = object.numResultsDelta;
    this.currency = object.currency;
    this.punchoutId = object.punchoutId;

    extendObservable(this, {
      // Minimums
      payRateMin: object.payRateMin || null,
      billRateMin: object.billRateMin || null,
      markupAmtMin: object.markupAmtMin || null,
      markupPctMin: object.markupPctMin || null,
      // Averages
      payRateAvg: object.payRateAvg || null,
      billRateAvg: object.billRateAvg || null,
      markupAmtAvg: object.markupAmtAvg || null,
      markupPctAvg: object.markupPctAvg || null,
      //Average Deltas
      payRateAvgDelta: object.payRateAvgDelta === null ? null : object.payRateAvgDelta,
      billRateAvgDelta: object.billRateAvgDelta === null ? null : object.billRateAvgDelta,
      markupPctAvgDelta:
        object.markupPctAvgDelta === null ? null : object.markupPctAvgDelta,
      // Mids
      payRateMid: object.payRateMid || null,
      billRateMid: object.billRateMid || null,
      markupAmtMid: object.markupAmtMid || null,
      markupPctMid: object.markupPctMid || null,
      //Maxes
      payRateMax: object.payRateMax || null,
      billRateMax: object.billRateMax || null,
      markupAmtMax: object.markupAmtMax || null,
      markupPctMax: object.markupPctMax || null,
      latestChange: object.latestChange
        ? new SearchResultsHistory(store, object.latestChange)
        : null,
      change: object.latestChange ? new SearchResultsChange(store, object) : null,
      //payrateAlert values
      payRateMinAlert: object.payRateMinAlert || null,
      payRateMidAlert: object.payRateMidAlert || null,
      payRateMaxAlert: object.payRateMaxAlert || null,
      payRateAvgAlert: object.payRateAvgAlert || null,
    });

    this.refreshChange = action(this.refreshChange.bind(this));
    this.createHistory = action(this.createHistory.bind(this));

    this.createHistory(object.history);
  }

  createHistory(payloadHistory: Object[]): void {
    this.history = [];

    if (!payloadHistory) return;

    this.history = payloadHistory.map(
      (historyItem) => new SearchResultsHistory(this, historyItem)
    );
  }

  refreshChange() {
    this.change = new SearchResultsChange(this.store, this);
  }
}

const DisclaimerTooltip = (props: Object) => {
  const minWagePopOver = (
    <Popover id="min-wage-disclaimer-popover" title="Important Information">
      {props.children}
    </Popover>
  );
  return (
    <OverlayTrigger trigger="click" placement="top" overlay={minWagePopOver} rootClose>
      <FontAwesomeIcon icon="info-circle" style={{ marginLeft: 3, cursor: "pointer" }} />
    </OverlayTrigger>
  );
};

const DefaultDisclaimerBody = (props: Object) => {
  return (
    <div>
      {props.children}
      <p className="text-xx-small">
        <a href={`mailto:${supportEmail}`} rel="noopener noreferrer">
          Contact Us
        </a>{" "}
        for more information.
      </p>
    </div>
  );
};

const DefaultDisclaimerTooltip = (props: Object) => (
  <DisclaimerTooltip>
    <DefaultDisclaimerBody>
      <p className="text-xx-small">
        Minimum wages may vary by location, role type, age, experience, company type, and
        other factors. If you are hiring at the minimum level, please confirm your local
        regulations and how they apply to your company.
      </p>
    </DefaultDisclaimerBody>
  </DisclaimerTooltip>
);

const AustraliaDisclaimerToolTip = (props: Object) => (
  <DisclaimerTooltip>
    <DefaultDisclaimerBody>
      <p className="text-xx-small">
        Minimum wages may vary by location, role type, age, experience, company type, and
        other factors. If you are hiring at the minimum level, please confirm your local
        regulations and how they apply to your company. To assist you, please refer to{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.fairwork.gov.au/awards-and-agreements/awards/list-of-awards"
        >
          https://www.fairwork.gov.au
        </a>
      </p>
    </DefaultDisclaimerBody>
  </DisclaimerTooltip>
);

export const MIN_WAGE_DISCLAIMERS = [
  {
    country: "India",
    tooltip: <DefaultDisclaimerTooltip />,
  },
  {
    country: "Australia",
    tooltip: <AustraliaDisclaimerToolTip />,
  },
];

export function MinWageDisclaimer(props: { country: string }): React.Node {
  const disclaimerConfig = MIN_WAGE_DISCLAIMERS.find((c) => c.country === props.country);
  return disclaimerConfig?.tooltip || null;
}

export default class Search {
  store: Object;
  id: number;
  searchId: number;
  buyrateId: number;
  buyrateName: string;
  rateCardId: ?number;
  rateCardName: ?string;
  createdBuyrate: Object;
  ownerLogin: string;
  label: string;
  title: string;
  category: string;
  location: Object;
  locationFullLabel: string;
  locationLabel: string;
  countryLabel: string;
  industry: Object;
  frequency: Object;
  created: Object;
  createdDisplay: string;
  lastUpdated: Object;
  lastUpdatedDisplay: string;
  lastUpdatedDateDisplay: string;
  region: ?Object;
  pageState: Object;
  viewState: { selected: boolean, editing: boolean, expanded: boolean };
  jobDescription: ?string;
  selectedLevelViewDisplayOption: number;
  selectedDataDisplayOption: number;
  selectedTabularDisplayOption: number;
  selectedLevelPeekDisplayOption: string;
  requestedResultTypes: { [id: string]: boolean };
  resultsByLevel: { [id: string]: ?SearchResults };
  buyRates: ?(BuyRate[]);
  currency: ?Currency;
  network: NetworkState;
  needsRatesUpdate: Boolean;
  canRevertLatestUpdate: boolean;
  showRates: boolean;
  showDeltas: boolean;
  showTable: boolean;
  showCards: boolean;
  showThreeLevelsView: boolean;
  showFiveLevelView: boolean;
  showPayRate: boolean;
  showMarkup: boolean;
  showBillRate: boolean;
  currencyType: string;
  hideMedian: boolean;
  hideMarkup: boolean;
  hideBillRate: boolean;
  needsSetAlertBtn: boolean;
  needsUpdateBtn: boolean;
  tags: Array<Object>;
  ratesData: ?Array<number>;
  isPtAdmin: boolean;
  isDraft: boolean;
  isGSS: boolean;
  workerTypeName: string;

  SHOW_THREE_LEVELS_VIEW_OPTION: number;
  SHOW_FIVE_LEVEL_VIEW_OPTION: number;
  SHOW_RATES_OPTION: number;
  SHOW_DELTAS_OPTION: number;
  SHOW_PAY_RATE_OPTION: string;
  SHOW_MARKUP_OPTION: string;
  SHOW_BILL_RATE_OPTION: string;
  SHOW_TABLE_OPTION: number;
  SHOW_CARDS_OPTION: number;
  MIN_WAGE_DISCLAIMER_COUNTRIES: string[];

  showMinWageDisclaimer: boolean;
  minWageDisclaimerTooltip: any;

  toggleEdit: () => void;
  toggleExpand: () => void;
  toggleSelected: (SyntheticEvent<HTMLElement>, ?any, ?any, ?boolean) => void;
  resetRates: () => void;
  setLastUpdateDateFromPayload: (Object) => void;
  getSearchResults: (?boolean, ?boolean, ?Date, ?Date) => void;
  setCurrencyType: (?string) => void;
  updateRates: (?boolean) => void;
  undoUpdateRates: (?boolean) => void;
  destroy: () => void;
  mapResults: (Object) => void;
  mapBuyRates: (Object) => void;
  locationDetail: () => string;
  handleLevelPeekDisplayOptionChange: (SyntheticInputEvent<HTMLInputElement>) => void;
  handleDataDisplayOptionChange: (SyntheticInputEvent<HTMLElement>) => void;
  handleLevelViewDisplayOptionChange: (SyntheticInputEvent<HTMLElement>) => void;
  handleTabularDisplayOptionChange: (SyntheticInputEvent<HTMLElement>) => void;
  updateDataDisplayOptionFlags: () => void;
  updateTabularDisplayOptionFlags: () => void;
  updateLevelPeekDisplayOptionFlags: () => void;
  // evalCanRevertLastUpdate: () => boolean;
  updateLevelViewDisplayOptionFlags: () => void;

  constructor(store: Object, object: Object) {
    this.SHOW_THREE_LEVELS_VIEW_OPTION = 1;
    this.SHOW_FIVE_LEVEL_VIEW_OPTION = 2;

    this.SHOW_RATES_OPTION = 1;
    this.SHOW_DELTAS_OPTION = 2;

    this.SHOW_TABLE_OPTION = 1;
    this.SHOW_CARDS_OPTION = 2;

    this.SHOW_PAY_RATE_OPTION = "payRate";
    this.SHOW_MARKUP_OPTION = "markupPct";
    this.SHOW_BILL_RATE_OPTION = "billRate";

    this.MIN_WAGE_DISCLAIMER_COUNTRIES = ["India"];

    const searchId = object.searchId || object.legacyId || object.databaseId || object.id;

    this.store = store;
    this.id = searchId;
    this.buyrateId = object.buyrateId || 0;
    this.buyrateName = object.buyrateName || "N/A";
    this.createdBuyrate = moment(object.createdBuyrate) || {};
    this.ownerLogin =
      object.createdBy && object.createdBy.username ? object.createdBy.username : "N/A";
    this.label = object.job.jobLabel || object.job.jobTitle || "N/A";
    this.title = object.job.jobTitle || "N/A";
    this.searchId = object.searchId;
    this.category = object.category || "N/A";
    this.location = {
      id: object.locationId,
      country: object.country,
      state: object.state,
      city: object.city,
    } || { id: 236, country: "US", state: "FL", city: "Miami" };
    this.locationFullLabel = object.locationFullLabel || "";
    this.locationLabel = object.locationLabel || "";
    this.countryLabel = object.countryLabel || "";
    this.industry = {
      id: object.industry.legacyId,
      title: object.industry.value,
    } || { id: 1, title: "Computers & Technology" };

    if (object.rateType === 1) {
      this.frequency = { id: 1, title: RATE_TYPE_OPTIONS_2_BY_ID[1].label };
    } else if (object.rateType === 2) {
      this.frequency = { id: 2, title: RATE_TYPE_OPTIONS_2_BY_ID[2].label };
    } else if (!object.rateType) {
      console.warn(`Search ${searchId}: property rateType missing. Defaulting.`);
      // console.log(JSON.stringify(object, null, 2));
      this.frequency = object.rateType || {
        id: 1,
        title: RATE_TYPE_OPTIONS_2_BY_ID[1].label,
      };
    } else {
      throw new Error(`Search ${searchId}: Unknown rateType value ${object.rateType}`);
    }

    this.created = object.createdDate ? moment(object.createdDate) : moment("2016-08-23");
    this.createdDisplay = `Created On ${this.created.format("MMMM D, YYYY")}`;
    this.lastUpdated = object.lastUpdated
      ? moment(object.lastUpdated)
      : moment("2016-08-30");
    this.lastUpdatedDateDisplay = this.lastUpdated.format("MMM, YYYY");
    this.region = object.region || null;
    this.pageState = object.pageState || {};
    // TODO: Reconcile this with the pageState/other viewstate properties on this...
    this.viewState = { selected: false, editing: false, expanded: false };
    this.jobDescription = object.jobDescription || object.job.jobDescription || null;
    this.currency = object.currency ? new Currency(store, object.currency) : null;
    this.needsSetAlertBtn = object.needsSetAlertBtn || false;
    this.needsUpdateBtn = object.needsUpdateBtn || true;
    // this.tags = object.tags || [];
    this.isPtAdmin = object.isPtAdmin || false;
    this.isDraft = object.isDraft || false;
    this.isGSS = object.isGlobalSupplierSearch || false;
    this.workerTypeName = object.workerTypeName || null;

    const tags = object.tags || [];
    const buyrates = object.buyrates || [];

    extendObservable(this, {
      showChanges: true,
      selectedLevelViewDisplayOption: this.SHOW_FIVE_LEVEL_VIEW_OPTION,
      showThreeLevelsView: false,
      showFiveLevelView: false,
      selectedDataDisplayOption: this.SHOW_RATES_OPTION,
      selectedTabularDisplayOption: this.SHOW_TABLE_OPTION,
      rateCardId: null,
      rateCardName: null,
      showRates: false,
      showDeltas: false,
      showTable: false,
      showCards: false,
      selectedLevelPeekDisplayOption: this.SHOW_PAY_RATE_OPTION,
      showPayRate: false,
      showMarkup: false,
      showBillRate: false,
      lastUpdatedDisplay: "",
      currencyType: "",
      lastUpdatedDateDisplay: "",
      tags: tags.map((tag) => new TagList(this, tag)) || [],
      requestedResultTypes: {
        payRate: false,
        markupPct: false,
        billRate: false,
      },
      hideMin: false,
      hideMedian: false,
      hideAverage: false,
      hideMax: false,
      hideMarkup: false,
      hideBillRate: false,
      // TODO: Change this to avoid using levelName
      resultsByLevel: {
        Junior: null,
        Mid: null,
        Senior: null,
        Lead: null,
        Guru: null,
      },
      needsRatesUpdate: Boolean(object.needsUpdate),
      canRevertLatestUpdate: Boolean(object.canRevert),
      buyRates: buyrates.map((buyRate) => new BuyRate(buyRate)),
      network: new NetworkState(),
      ratesData: null,
      showMinWageDisclaimer: computed(() => {
        const { location, region } = this;
        let country = "";
        if (this.countryLabel) country = this.countryLabel;
        else if (!!location && !!location.country) country = location.country;
        else if (!!region && !!region.country) country = region.country.name;

        return !!MIN_WAGE_DISCLAIMERS.find((c) => c.country === country);
      }),
      minWageDisclaimerTooltip: computed(() => {
        const { location, region } = this;
        let country = "";
        if (this.countryLabel) country = this.countryLabel;
        else if (!!location && !!location.country) country = location.country;
        else if (!!region && !!region.country) country = region.country.name;

        const disclaimerConfig = MIN_WAGE_DISCLAIMERS.find((c) => c.country === country);

        if (!disclaimerConfig) return null;

        return disclaimerConfig.tooltip;
      }),
    });

    this.toggleEdit = action(this.toggleEdit.bind(this));
    this.toggleExpand = action(this.toggleExpand.bind(this));
    this.toggleSelected = action(this.toggleSelected.bind(this));
    this.handleLevelPeekDisplayOptionChange = action(
      this.handleLevelPeekDisplayOptionChange.bind(this)
    );
    this.handleDataDisplayOptionChange = action(
      this.handleDataDisplayOptionChange.bind(this)
    );
    this.handleLevelViewDisplayOptionChange = action(
      this.handleLevelViewDisplayOptionChange.bind(this)
    );
    this.handleTabularDisplayOptionChange = action(
      this.handleTabularDisplayOptionChange.bind(this)
    );
    this.getSearchResults = action(this.getSearchResults.bind(this));
    this.setCurrencyType = action(this.setCurrencyType.bind(this));
    this.destroy = action(this.destroy.bind(this));
    this.mapResults = this.mapResults.bind(this);
    this.mapBuyRates = this.mapBuyRates.bind(this);
    this.locationDetail = action(this.locationDetail.bind(this));
    this.updateDataDisplayOptionFlags = action(
      this.updateDataDisplayOptionFlags.bind(this)
    );
    this.updateTabularDisplayOptionFlags = action(
      this.updateTabularDisplayOptionFlags.bind(this)
    );
    this.updateLevelPeekDisplayOptionFlags = action(
      this.updateLevelPeekDisplayOptionFlags.bind(this)
    );
    this.updateRates = action(this.updateRates.bind(this));
    this.undoUpdateRates = action(this.undoUpdateRates.bind(this));
    this.resetRates = action(this.resetRates.bind(this));
    this.setLastUpdateDateFromPayload = action(
      this.setLastUpdateDateFromPayload.bind(this)
    );
    // this.evalCanRevertLastUpdate = action(this.evalCanRevertLastUpdate.bind(this));
    this.updateLevelViewDisplayOptionFlags = action(
      this.updateLevelViewDisplayOptionFlags.bind(this)
    );

    if (moment().format("YYYY-MM") === this.lastUpdated.format("YYYY-MM")) {
      this.lastUpdatedDisplay = "This Month";
    } else if (
      moment().subtract(1, "months").format("YYYY-MM") ===
      this.lastUpdated.format("YYYY-MM")
    ) {
      this.lastUpdatedDisplay = "Last Month";
    } else {
      this.lastUpdatedDisplay = `${this.lastUpdated
        .fromNow()
        .replace(/\b\w/g, (l) => l.toUpperCase())}`;
    }

    if (object.rateCard) {
      if (object.rateCard.legacyId) {
        this.rateCardId = object.rateCard.legacyId;
      }
      if (object.rateCard.name) {
        this.rateCardName = object.rateCard.name;
      }
    }

    if (object.results) {
      object.results.edges.map(this.mapResults);
    }

    // if (object.buyrates) {
    //   object.buyrates.map(this.mapBuyRates);
    // }

    if (object.marketrates) {
      object.marketrates.forEach((r, i) => {
        const marketchanges = object.marketchanges || [];
        this.resultsByLevel[r.level.value] = new SearchResults(this, {
          node: {
            currency: object.currency,
            latestChange: marketchanges.find(
              (change) => change.level.value === r.level.value
            ),
            ...r,
          },
        });
      });
    }

    if (object.ratesData) {
      this.ratesData = object.ratesData;
    }

    // this.canRevertLatestUpdate = this.evalCanRevertLastUpdate();
    this.updateDataDisplayOptionFlags();
    this.updateTabularDisplayOptionFlags();
    this.updateLevelPeekDisplayOptionFlags();
    this.updateLevelViewDisplayOptionFlags();
  }

  // evalCanRevertLastUpdate() {
  //   if (!this.resultsByLevel) return false;
  //
  //   for (let level in this.resultsByLevel) {
  //     if (!this.resultsByLevel.hasOwnProperty(level)) continue;
  //
  //     if (!this.resultsByLevel[level]) continue;
  //
  //     if (this.resultsByLevel[level].canRevertLatestUpdate) return true;
  //   }
  //
  //   return false;
  // }

  resetRates() {
    // this.resultsByLevel = {
    //   Junior: null,
    //   Mid: null,
    //   Senior: null,
    //   Lead: null,
    //   Guru: null,
    // };
    this.requestedResultTypes = {
      payRate: false,
      markupPct: false,
      billRate: false,
    };
  }

  setLastUpdateDateFromPayload(payloadSearch: Object) {
    this.lastUpdated = moment();
    if (payloadSearch) this.lastUpdated = moment(payloadSearch);
    if (moment().format("YYYY-MM") === this.lastUpdated.format("YYYY-MM"))
      this.lastUpdatedDisplay = "This Month";
    else if (
      moment().subtract(1, "months").format("YYYY-MM") ===
      this.lastUpdated.format("YYYY-MM")
    )
      this.lastUpdatedDisplay = "Last Month";
    else
      this.lastUpdatedDisplay = `${this.lastUpdated
        .fromNow()
        .replace(/\b\w/g, (l) => l.toUpperCase())}`;
    this.lastUpdatedDateDisplay = this.lastUpdated.format("MMM, YYYY");
  }

  updateDataDisplayOptionFlags() {
    this.showRates = this.selectedDataDisplayOption === this.SHOW_RATES_OPTION;
    this.showDeltas = this.selectedDataDisplayOption === this.SHOW_DELTAS_OPTION;
  }

  updateTabularDisplayOptionFlags() {
    this.showTable = this.selectedTabularDisplayOption === this.SHOW_TABLE_OPTION;
    this.showCards = this.selectedTabularDisplayOption === this.SHOW_CARDS_OPTION;
  }

  updateLevelViewDisplayOptionFlags() {
    this.showThreeLevelsView =
      this.selectedLevelViewDisplayOption === this.SHOW_THREE_LEVELS_VIEW_OPTION;
    this.showFiveLevelView =
      this.selectedLevelViewDisplayOption === this.SHOW_FIVE_LEVEL_VIEW_OPTION;

    this.hideMedian = this.showThreeLevelsView;
    if (this.showThreeLevelsView) {
      this.showPayRate = true;
      this.showMarkup = false;
      this.showBillRate = false;
    }
  }

  updateLevelPeekDisplayOptionFlags() {
    this.showPayRate = this.selectedLevelPeekDisplayOption === this.SHOW_PAY_RATE_OPTION;
    this.showMarkup = this.selectedLevelPeekDisplayOption === this.SHOW_MARKUP_OPTION;
    this.showBillRate =
      this.selectedLevelPeekDisplayOption === this.SHOW_BILL_RATE_OPTION;
  }

  toggleEdit() {
    this.viewState.editing = !this.viewState.editing;
  }

  toggleExpand() {
    this.viewState.expanded = !this.viewState.expanded;
    if (this.viewState.expanded) {
      this.getSearchResults();
    }
  }

  toggleSelected(
    e: SyntheticEvent<HTMLElement>,
    ignore1: any = null,
    ignore2: any = null,
    setValue: ?boolean = null
  ) {
    const viewState = this.store.searchesViewState.get(this.id);

    if (setValue !== null) {
      viewState.selected = setValue;
    } else {
      viewState.selected = !viewState.selected;

      if (viewState.selected === false && this.store.allSelected) {
        this.store.allSelected = false;
      }
    }
  }

  handleDataDisplayOptionChange(e: SyntheticInputEvent<HTMLElement>) {
    this.selectedDataDisplayOption = parseInt(e.target.value, 10);
    this.updateDataDisplayOptionFlags();
  }

  handleLevelViewDisplayOptionChange(e: SyntheticInputEvent<HTMLElement>) {
    this.selectedLevelViewDisplayOption = parseInt(e.target.value, 10);
    this.updateLevelViewDisplayOptionFlags();

    this.getSearchResults();

    this.hideMedian = this.showThreeLevelsView;
    this.hideMarkup = this.showThreeLevelsView;
    this.hideBillRate = this.showThreeLevelsView;
  }

  handleTabularDisplayOptionChange(e: SyntheticInputEvent<HTMLElement>) {
    this.selectedTabularDisplayOption = parseInt(e.target.value, 10);
    this.updateTabularDisplayOptionFlags();
  }

  destroy() {
    this.store.searches.remove(this);
  }

  mapResults(edge: Object) {
    let payloadResult: Object = edge;
    let searchResult: ?SearchResults =
      this.resultsByLevel[payloadResult.node.level.value];
    if (searchResult) {
      if (payloadResult.history) searchResult.createHistory(payloadResult.node.history);

      if (searchResult.latestChange && payloadResult.latestChange)
        Object.assign(searchResult.latestChange, payloadResult.node.latestChange);

      Object.assign(searchResult, payloadResult.node);
      searchResult.refreshChange();
    } else {
      this.resultsByLevel[payloadResult.node.level.value] = new SearchResults(
        this,
        payloadResult
      );
    }
  }

  mapBuyRates(dataItem: Object) {
    if (this.buyRates) {
      this.buyRates.push(new BuyRate(dataItem));
    }
  }

  locationDetail() {
    if (this.locationFullLabel) {
      return `${this.region ? "Region: " : ""}${this.locationFullLabel}`;
    }

    let location = this.location.country;

    if (this.region && this.region.name) {
      location = `Region: ${this.region.name}`;
    } else if (this.locationLabel) {
      location = this.locationLabel;
    } else {
      const parts = [this.location.city, this.location.state, this.location.country];
      location = parts.filter((item) => Boolean(item) && item !== "").join(", ");
    }

    return location;
  }

  toJS() {
    return { id: this.id, label: this.label };
  }

  setCurrencyType(currencyType: string = "") {
    this.currencyType = currencyType;
  }

  async getSearchResults(
    fullReload?: boolean = true,
    hardReload?: boolean = false,
    historyFrom?: ?Date = null,
    historyTo?: ?Date = null
  ) {
    // console.log("getSearchResults");
    if (hardReload) {
      this.resetRates();
    }
    let vars = {};
    vars.searchId = this.id;
    if (fullReload) {
      if (
        this.requestedResultTypes[this.SHOW_PAY_RATE_OPTION] &&
        this.requestedResultTypes[this.SHOW_MARKUP_OPTION] &&
        this.requestedResultTypes[this.SHOW_BILL_RATE_OPTION]
      ) {
        // all results already here, don't request again
        return;
      }
    }
    if (this.requestedResultTypes[this.selectedLevelPeekDisplayOption] === true) {
      // Results already there, don't do another request.
      return;
    }

    this.network.loading = true;
    let res: ?Object = null;

    let query: string = fullReload
      ? queries.fullReload(this.currencyType, historyFrom, historyTo)
      : queries[this.selectedLevelPeekDisplayOption](this.currencyType);

    if (this.currencyType && this.currencyType !== "Default")
      vars.convertTo = this.currencyType;
    if (this.isPtAdmin) {
      vars.section = "ADMIN";
    } else {
      vars.section = "USER";
    }
    if (historyFrom) vars.resultsHistoryFrom = historyFrom;
    if (historyTo) vars.resultsHistoryTo = historyTo;

    try {
      res = await this.store.fetchGraphQL(query, vars);
    } catch (e) {
      this.network.handleError("Getting Search Rates", e);
      // TODO: Display user friendly error message
      return;
    }

    if (res.errors) {
      console.error("Errors", res.errors);
      this.network.loading = false;
      if (
        res.errors[0].message ===
        "Access reserved for authenticated users or services. Permission denied."
      ) {
        window.location.reload();
      }

      return;
    }

    runInAction(
      action("getSearchResults--success", () => {
        // console.log("getSearchResults--success:", res);
        this.network.loading = false;
        this.network.error = null;
        // if (this.network.logGraphQLError('Search results query', res)) {
        //   // TODO: Display user friendly error message
        //   return;
        // }

        // NOTE: Flow refinement
        if (res == null) {
          return;
        }

        // update rates and history
        const object = res.data.viewer.savedsearch;
        if (object.marketrates) {
          object.marketrates.forEach((r, i) => {
            const marketchanges = object.marketchanges || [];
            this.resultsByLevel[r.level.value] = new SearchResults(this, {
              node: {
                currency: object.currency,
                latestChange: marketchanges.find(
                  (change) => change.level.value === r.level.value
                ),
                ...r,
              },
            });
          });
        }

        this.needsRatesUpdate = res.data.viewer.savedsearch.needsUpdate;
        this.canRevertLatestUpdate = res.data.viewer.savedsearch.canRevert;
        this.currency = new Currency(this, res.data.viewer.savedsearch.currency);
        if (fullReload) {
          this.requestedResultTypes[this.SHOW_PAY_RATE_OPTION] = true;
          this.requestedResultTypes[this.SHOW_MARKUP_OPTION] = true;
          this.requestedResultTypes[this.SHOW_BILL_RATE_OPTION] = true;
        } else {
          this.requestedResultTypes[this.selectedLevelPeekDisplayOption] = true;
        }
      })
    );
  }

  async handleLevelPeekDisplayOptionChange(e: SyntheticInputEvent<HTMLInputElement>) {
    this.selectedLevelPeekDisplayOption = e.target.value;
    this.updateLevelPeekDisplayOptionFlags();
    if (!this.requestedResultTypes[this.selectedLevelPeekDisplayOption]) {
      await this.getSearchResults();
    }
  }

  async updateRates(fullReload: boolean = false) {
    this.network.loading = true;

    let payload: ?Object;
    try {
      payload = await this.store.fetchGraphQL(updateRatesQuery, { searchId: this.id });
    } catch (e) {
      this.network.handleError("Updating Search Rates", e);
      // TODO: Display user friendly error message
      return;
    }

    runInAction("updateRates--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("updateRatesOnSingleSearch", payload)) {
        // TODO: Display user friendly error message
        return;
      }

      // NOTE: Flow refinement
      if (payload == null) {
        return;
      }

      this.setLastUpdateDateFromPayload(
        payload.data.updateSingleSearchMarketRate.lastUpdatedTime
      );
      // reset and reload rates
      this.resetRates();
      this.getSearchResults(fullReload);
    });
  }

  async undoUpdateRates(fullReload: boolean = false) {
    this.network.loading = true;

    let payload: ?Object;
    try {
      payload = await this.store.fetchGraphQL(undoUpdateRatesQuery, {
        searchId: this.id,
      });
    } catch (e) {
      this.network.handleError("Undo Last Update Rates on Search", e);
      // TODO: Display user friendly error message
      return;
    }

    runInAction("undoUpdateRates--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("undoUpdateRatesOnSingleSearch", payload)) {
        // TODO: Display user friendly error message
        return;
      }

      // NOTE: Flow refinement
      if (payload == null) {
        return;
      }

      this.setLastUpdateDateFromPayload(
        payload.data.undoUpdateSingleSearchMarketRate.lastUpdatedTime
      );

      // reset and reload rates
      this.resetRates();
      this.getSearchResults(fullReload);
    });
  }

  static fromJS(store: Object, object: Object) {
    return new Search(store, object);
  }
}

// WEBPACK FOOTER //
// src/models/Search.js
